/** Dominio del estado de servicio: tipos ServiceStatus/ServiceAction, mapa de acciones y transiciones, y colores/labels por estado (sin UI). */
import { AccionServicio, EstadoServicio } from "@/types";

export type ServiceStatus =
  | "CREADA"
  | "EN PROCESO"
  | "PAUSADO"
  | "FINALIZADA"
  | "CANCELADA"
  | "ESPERANDO INSUMO"
  | "ESPERANDO SERVICIO DE TERCERO"
  | "ESPERANDO APROBACION"
  | "ENTREGADO";

export type ServiceAction = "iniciar" | "pausar" | "reanudar" | "finalizar" | "cancelar";

export const ACCION_ORDEN_POR_UI: Partial<Record<ServiceAction, AccionServicio>> = {
  iniciar: AccionServicio.Play,
  pausar: AccionServicio.Pausa,
  reanudar: AccionServicio.Reinicio,
  finalizar: AccionServicio.Detener,
};

export const DESTINOS_POR_ACCION: Partial<
  Record<ServiceAction, { value: EstadoServicio; label: string }[]>
> = {
  pausar: [
    { value: EstadoServicio.EsperandoInsumo, label: "Esperando insumo" },
    { value: EstadoServicio.EsperandoServicioTercero, label: "Esperando servicio de tercero" },
    { value: EstadoServicio.EsperandoAprobacion, label: "Esperando aprobación" },
    { value: EstadoServicio.Pausado, label: "Pausado" },
  ],
  finalizar: [
    { value: EstadoServicio.Finalizada, label: "Finalizada" },
    { value: EstadoServicio.Cancelada, label: "Cancelada" },
  ],
};

export const ESTADO_UI_POR_DESTINO: Partial<Record<EstadoServicio, ServiceStatus>> = {
  [EstadoServicio.EsperandoInsumo]: "ESPERANDO INSUMO",
  [EstadoServicio.EsperandoServicioTercero]: "ESPERANDO SERVICIO DE TERCERO",
  [EstadoServicio.EsperandoAprobacion]: "ESPERANDO APROBACION",
  [EstadoServicio.Pausado]: "PAUSADO",
  [EstadoServicio.Finalizada]: "FINALIZADA",
  [EstadoServicio.Cancelada]: "CANCELADA",
};

export const TITULO_ACCION: Record<ServiceAction, string> = {
  iniciar: "Iniciar servicio",
  reanudar: "Reanudar servicio",
  pausar: "Pausar servicio",
  finalizar: "Finalizar servicio",
  cancelar: "Cancelar servicio",
};

export const ACCIONES_POR_ESTADO: Record<ServiceStatus, ServiceAction[]> = {
  CREADA: ["iniciar", "cancelar"],
  "EN PROCESO": ["pausar", "finalizar"],
  PAUSADO: ["reanudar", "finalizar"],
  "ESPERANDO INSUMO": ["reanudar", "finalizar"],
  "ESPERANDO SERVICIO DE TERCERO": ["reanudar", "finalizar"],
  "ESPERANDO APROBACION": ["reanudar", "finalizar"],
  FINALIZADA: [],
  CANCELADA: [],
  ENTREGADO: [],
};

export const TRANSICIONES: Record<ServiceStatus, Record<ServiceAction, ServiceStatus>> = {
  CREADA: {
    iniciar: "EN PROCESO",
    pausar: "CREADA",
    reanudar: "CREADA",
    finalizar: "CREADA",
    cancelar: "CANCELADA",
  },
  "EN PROCESO": {
    iniciar: "EN PROCESO",
    pausar: "PAUSADO",
    reanudar: "EN PROCESO",
    finalizar: "FINALIZADA",
    cancelar: "CANCELADA",
  },
  PAUSADO: {
    iniciar: "EN PROCESO",
    pausar: "PAUSADO",
    reanudar: "EN PROCESO",
    finalizar: "FINALIZADA",
    cancelar: "CANCELADA",
  },
  "ESPERANDO INSUMO": {
    iniciar: "EN PROCESO",
    pausar: "ESPERANDO INSUMO",
    reanudar: "EN PROCESO",
    finalizar: "FINALIZADA",
    cancelar: "CANCELADA",
  },
  "ESPERANDO SERVICIO DE TERCERO": {
    iniciar: "EN PROCESO",
    pausar: "ESPERANDO SERVICIO DE TERCERO",
    reanudar: "EN PROCESO",
    finalizar: "FINALIZADA",
    cancelar: "CANCELADA",
  },
  "ESPERANDO APROBACION": {
    iniciar: "EN PROCESO",
    pausar: "ESPERANDO APROBACION",
    reanudar: "EN PROCESO",
    finalizar: "FINALIZADA",
    cancelar: "CANCELADA",
  },
  FINALIZADA: {
    iniciar: "FINALIZADA",
    pausar: "FINALIZADA",
    reanudar: "FINALIZADA",
    finalizar: "FINALIZADA",
    cancelar: "FINALIZADA",
  },
  CANCELADA: {
    iniciar: "CANCELADA",
    pausar: "CANCELADA",
    reanudar: "CANCELADA",
    finalizar: "CANCELADA",
    cancelar: "CANCELADA",
  },
  ENTREGADO: {
    iniciar: "ENTREGADO",
    pausar: "ENTREGADO",
    reanudar: "ENTREGADO",
    finalizar: "ENTREGADO",
    cancelar: "ENTREGADO",
  },
};

export const ESTADO_CONFIG: Record<ServiceStatus, { color: string; bg: string; label: string }> = {
  CREADA: { color: "#1677ff", bg: "rgba(22, 119, 255, 0.08)", label: "Por empezar" },
  "EN PROCESO": { color: "#52c41a", bg: "rgba(82, 196, 26, 0.10)", label: "En curso" },
  PAUSADO: { color: "#d48806", bg: "rgba(212, 136, 6, 0.08)", label: "Pausado" },
  "ESPERANDO INSUMO": { color: "#fa8c16", bg: "rgba(250, 140, 22, 0.10)", label: "Insumo" },
  "ESPERANDO SERVICIO DE TERCERO": {
    color: "#722ed1",
    bg: "rgba(114, 46, 209, 0.10)",
    label: "Tercero",
  },
  "ESPERANDO APROBACION": { color: "#faad14", bg: "rgba(250, 173, 20, 0.12)", label: "Aprobación" },
  FINALIZADA: { color: "#389e0d", bg: "rgba(56, 158, 13, 0.10)", label: "Finalizada" },
  CANCELADA: { color: "#f5222d", bg: "rgba(245, 34, 45, 0.08)", label: "Cancelada" },
  ENTREGADO: { color: "#13c2c2", bg: "rgba(19, 194, 194, 0.10)", label: "Entregado" },
};

const ESTADO_POR_NUMERO: Record<number, ServiceStatus> = {
  [EstadoServicio.Creada]: "CREADA",
  [EstadoServicio.Pendiente]: "CREADA",
  [EstadoServicio.Cancelada]: "CANCELADA",
  [EstadoServicio.EsperandoInsumo]: "ESPERANDO INSUMO",
  [EstadoServicio.Finalizada]: "FINALIZADA",
  [EstadoServicio.EnProceso]: "EN PROCESO",
  [EstadoServicio.EsperandoServicioTercero]: "ESPERANDO SERVICIO DE TERCERO",
  [EstadoServicio.EsperandoAprobacion]: "ESPERANDO APROBACION",
  [EstadoServicio.Entregado]: "FINALIZADA",
  [EstadoServicio.Pausado]: "PAUSADO",
};

const ESTADOS_CONOCIDOS = Object.keys(ESTADO_CONFIG) as ServiceStatus[];

/** Índice por clave "compacta" (sin espacios ni signos): el backend manda "EnProceso", "EnProceso" -> ENPROCESO -> "EN PROCESO". */
const ESTADO_POR_CLAVE = ESTADOS_CONOCIDOS.reduce<Record<string, ServiceStatus>>((acc, estado) => {
  acc[estado.toUpperCase().replace(/[^A-Z0-9]/g, "")] = estado;
  return acc;
}, {});

export const normalizarEstado = (nombre?: string | null, numero?: number): ServiceStatus => {
  if (nombre) {
    const limpio = nombre.trim().toUpperCase();
    if ((ESTADOS_CONOCIDOS as string[]).includes(limpio)) return limpio as ServiceStatus;
    const clave = limpio.replace(/[^A-Z0-9]/g, "");
    if (ESTADO_POR_CLAVE[clave]) return ESTADO_POR_CLAVE[clave];
  }
  if (typeof numero === "number") return ESTADO_POR_NUMERO[numero] ?? "CREADA";
  return (nombre?.trim().toUpperCase() as ServiceStatus) || "CREADA";
};
